import { safeConnectCtx, ctxOk } from '../bufferTransport/ctxUtil';
// src/features/services/media/hooks/mixEngine/orchestrator/graphBuilder.ts

/** Flags that can influence routing (reserved for future use). */
export interface GraphFlags {
  eqEnabled: boolean;
  eqHpfEnabled: boolean;
  eqLpfEnabled: boolean;
  freqResponseEnabled: boolean;
}

/** Master graph owned by ensureMasterChain() elsewhere. */
export interface MasterGraph {
  ctx: AudioContext;
  masterGain: GainNode;
  analyser?: AnalyserNode;
  filters?: {
    lowpass: BiquadFilterNode;
    highpass: BiquadFilterNode;
  };
}

/** Per-stem graph nodes. */
export interface StemGraph {
  role: string;
  el: HTMLAudioElement;
  source: MediaElementAudioSourceNode;
  gain: GainNode;
  analyser: AnalyserNode;
  // Optional placeholders if you later insert nodes
  worklet?: { node: AudioWorkletNode } | null;
  delay?: DelayNode | null;
}

/**
 * Build a single stem:
 * <audio> → MediaElementAudioSourceNode → Gain → Analyser
 *
 * NOTE: A given <audio> element can only be wrapped by createMediaElementSource ONCE.
 * To be resilient during hot reloads, we cache the created source on the element.
 */
export async function buildStem(
  ctx: AudioContext,
  role: string,
  el: HTMLAudioElement,
  opts?: { gain?: number; useRb?: boolean } // useRb reserved for future insertion of a worklet
): Promise<StemGraph> {
  const anyEl = el as any;

  let source: MediaElementAudioSourceNode;
  if (anyEl.__mediaSource && anyEl.__mediaSource.context === ctx) {
    // Reuse the cached source if it belongs to this AudioContext
    source = anyEl.__mediaSource as MediaElementAudioSourceNode;
  } else {
    source = ctx.createMediaElementSource(el);
    anyEl.__mediaSource = source; // cache on element to avoid duplicate creation
  }

  const gain = ctx.createGain();
  gain.gain.value = opts?.gain ?? 1.0;

  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.17;

  // Wire local chain: source → gain → analyser
  try {
    // Avoid duplicate connections if hot reloaded
    source.disconnect();
  } catch {}
  try { safeConnectCtx(ctx, source, gain); } catch {}

  try {
    gain.disconnect();
  } catch {}
  try { safeConnectCtx(ctx, gain, analyser); } catch {}

  return {
    role,
    el,
    source,
    gain,
    analyser,
    worklet: null,
    delay: null,
  };
}

/**
 * Connect all stems to the master chain.
 * Default route: stem.gain → master.masterGain
 *
 * If you later want per-stem EQ or to insert a worklet between gain and analyser,
 * this is the place to adjust routing.
 */
export function connect(
  ctx: AudioContext,
  master: MasterGraph,
  stems: StemGraph[],
  _flags: GraphFlags
): void {
  for (const stem of stems) {
    try {
      stem.gain.disconnect();
    } catch {}

    // If your ensureMasterChain() already wired HPF/LPF/analyser on master,
    // the correct single target is master.masterGain:
  if (ctxOk(ctx)) { try { safeConnectCtx(ctx, stem.gain, master.masterGain); } catch {} }
  }
}

/**
 * Disconnect all stems cleanly.
 * Does not tear down the master chain (owned by ensureMasterChain()).
 */
export function disconnectAll(
  _master: MasterGraph | null | undefined,
  stems: StemGraph[]
): void {
  for (const stem of stems) {
    // Gain → (downstream)
    try { stem.gain.disconnect(); } catch {}

    // Source → Gain
    try { stem.source.disconnect(); } catch {}

    // Analyser (in case you connected it elsewhere)
    try { stem.analyser.disconnect(); } catch {}

    // Optional nodes
    try { stem.worklet?.node.disconnect(); } catch {}
    try { stem.delay?.disconnect(); } catch {}
  }
  // console.debug('[graphBuilder] disconnected all stems');
}
